import { TFunction } from 'i18next';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
    cookiePolicyPath,
    dashboardPagePath,
    faqPagePath,
    featureAppLicensesPagePath,
    identityBrokerErrorPagePath,
    landingPagePath,
    legalNoticePath,
    privacyPolicyPath,
    myProfilePagePath,
    myRequestPagePath,
    notAuthorizedPagePath,
    notFoundPagePath,
    postboxPagePath,
    registrationPagePath,
    logoutPath,
} from 'components/navigation/paths';

import {
    NavigationBar as NavigationBarShared,
    SiteNavItemPropsList,
    SiteNavProps,
    useAnalyticsActionTracker,
    useOidcAuthentication,
} from '@cp-shared-19/frontend-ui';

import { LanguageSwitcher } from './language-switcher/LanguageSwitcher';

import { useLogin } from 'auth/useLogin';
import { currentBrand } from 'config';
import { prependBrandInProduction } from '../../utils';

const pagesWithoutNavbar = [registrationPagePath(), featureAppLicensesPagePath()];

const pagesWithoutStaticHeader = [
    landingPagePath(),
    faqPagePath(),
    cookiePolicyPath(),
    legalNoticePath(),
    notAuthorizedPagePath(),
    notFoundPagePath(),
    privacyPolicyPath(),
    registrationPagePath(),
    logoutPath(),
];

function publicNavigationLinks(t: TFunction, login: () => Promise<void>): SiteNavItemPropsList {
    return [
        {
            url: landingPagePath(),
            label: t('navigation.home'),
            href: '#',
        },
        {
            url: faqPagePath(),
            label: t('navigation.faq'),
            href: '#',
        },
        {
            label: t('navigation.login'),
            onClick: login,
            href: '#',
        },
    ];
}

function privateNavigationLinks(t: TFunction, logout: () => Promise<void>): SiteNavItemPropsList {
    return [
        {
            url: dashboardPagePath(),
            label: t('navigation.dashboard'),
            href: '#',
        },
        {
            url: myProfilePagePath(),
            label: t('navigation.profile'),
            href: '#',
        },
        {
            url: postboxPagePath(),
            label: t('navigation.postbox'),
            href: '#',
        },
        {
            url: myRequestPagePath(),
            label: t('navigation.request'),
            href: '#',
        },
        {
            url: faqPagePath(),
            label: t('navigation.faq'),
            href: '#',
        },
        {
            label: t('navigation.logout'),
            onClick: logout,
            href: '#',
        },
    ];
}

export const NavigationBar: React.FC = () => {
    const { t } = useTranslation('navigation');
    const location = useLocation();
    const { isAuthenticated, logout } = useOidcAuthentication();
    const loginFn = useLogin();
    const { onAction: onLogoutAction } = useAnalyticsActionTracker('logout');
    const currentPageName = location.pathname.split('/')[1];

    const logoutFn = useCallback(() => {
        onLogoutAction(currentPageName);
        return logout({ post_logout_redirect_uri: window.location.origin + logoutPath() + '?logout-type=manual' });
    }, [logout, onLogoutAction, currentPageName]);
    const currentPathName = location.pathname;

    useEffect(() => {
        const toggle = document.querySelector<HTMLButtonElement>('.c-site-nav__toggle');
        if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
            toggle.click();
        }
    }, [currentPathName]);

    const navigationItems = useMemo(() => {
        if (currentPathName === identityBrokerErrorPagePath()) {
            return [];
        }
        return (isAuthenticated ? privateNavigationLinks(t, logoutFn) : publicNavigationLinks(t, loginFn)).map(
            navItem => ({
                ...{ isActive: currentPathName === navItem.url },
                ...navItem,
            }),
        );
    }, [isAuthenticated, logoutFn, loginFn, t, currentPathName]);

    const hidden = pagesWithoutNavbar.includes(currentPathName);
    const withStaticHeader = !pagesWithoutStaticHeader.includes(currentPathName);

    const navigation: SiteNavProps = {
        navigationItems,
    };

    return (
        <NavigationBarShared
            languageSwitcher={<LanguageSwitcher />}
            logoSource={prependBrandInProduction(`/contents/${currentBrand}/logo.svg`) || ''}
            logoAltText={t(`navigation.${isAuthenticated ? 'dashboard' : 'home'}`)}
            navigation={navigation}
            withStaticHeader={withStaticHeader}
            hidden={hidden}
            onLogoClickPath={isAuthenticated ? dashboardPagePath() : landingPagePath()}
        />
    );
};
